import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookingStatus, SLOT_MINUTES, isOnSlotBoundary } from '@fleet/shared';
import { bookingsApi } from '../api/bookings';
import { formatRange } from '../calendar/calendar-geometry';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { DateTimeInput, Field } from '../ui/Field';
import { ErrorNotice } from '../ui/ErrorNotice';
import { combine, dateValue, timeValue } from '../ui/datetime';

interface Props {
  booking: {
    id: string;
    bookingNumber: string;
    startsAt: string;
    endsAt: string;
    status: BookingStatus;
    /** Manager dieser Fahrzeuggruppe dürfen auch verlängern. */
    canExtend: boolean;
  };
  onClose: () => void;
}

/**
 * Zeiten einer bestehenden Buchung ändern.
 *
 * Der Dialog macht die Regel sichtbar, statt sie erst beim Speichern
 * durchzusetzen: Vor der Freigabe ist alles möglich, danach nur noch
 * Verkürzen. Ein Formular, das ein späteres Ende erst annimmt und dann mit
 * einem Fehler zurückkommt, lässt den Eindruck zurück, etwas sei kaputt.
 */
export function RescheduleDialog({ booking, onClose }: Props) {
  const queryClient = useQueryClient();

  const original = { start: new Date(booking.startsAt), end: new Date(booking.endsAt) };
  /**
   * Nur verkürzen darf, wer die Buchung nicht selbst freigeben könnte.
   *
   * Für Manager der Fahrzeuggruppe ist die Einschränkung sinnlos: Sie könnten
   * eine Verlängerung im nächsten Schritt genehmigen.
   */
  const shrinkOnly = booking.status !== BookingStatus.PENDING_APPROVAL && !booking.canExtend;

  const [startDate, setStartDate] = useState(dateValue(original.start));
  const [startTime, setStartTime] = useState(timeValue(original.start));
  const [endDate, setEndDate] = useState(dateValue(original.end));
  const [endTime, setEndTime] = useState(timeValue(original.end));

  const start = combine(startDate, startTime);
  const end = combine(endDate, endTime);

  const problems: string[] = [];
  if (!start || !end) problems.push('Bitte Datum und Uhrzeit vollständig angeben.');
  if (start && end && end <= start) problems.push('Das Ende muss nach dem Beginn liegen.');
  if ((start && !isOnSlotBoundary(start)) || (end && !isOnSlotBoundary(end))) {
    problems.push(`Zeiten müssen auf ${SLOT_MINUTES}-Minuten-Schritte fallen.`);
  }
  if (shrinkOnly && start && start < original.start) {
    problems.push(
      'Der Beginn kann nicht vorgezogen werden — die Freigabe galt ab dem bisherigen Zeitpunkt.',
    );
  }
  if (shrinkOnly && end && end > original.end) {
    problems.push(
      'Das Ende kann nicht nach hinten geschoben werden. Für mehr Zeit bitte neu buchen.',
    );
  }

  const unchanged =
    start?.getTime() === original.start.getTime() && end?.getTime() === original.end.getTime();

  const save = useMutation({
    mutationFn: () => bookingsApi.reschedule(booking.id, start!, end!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
      await queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      await queryClient.invalidateQueries({ queryKey: ['all-bookings'] });
      onClose();
    },
  });

  return (
    <Modal
      title="Zeiten ändern"
      description={`${booking.bookingNumber} · bisher ${formatRange(original.start, original.end)}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <p
          className={[
            'rounded-md border px-3 py-2 text-sm',
            shrinkOnly
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-slate-200 bg-slate-50 text-slate-600',
          ].join(' ')}
        >
          {shrinkOnly
            ? 'Diese Buchung ist bereits freigegeben — sie lässt sich nur verkürzen. Verkürzen braucht keine neue Freigabe. Wer mehr Zeit braucht, legt eine zusätzliche Buchung an.'
            : booking.status === BookingStatus.PENDING_APPROVAL
              ? 'Diese Buchung wartet noch auf Freigabe — die Zeiten lassen sich frei verschieben.'
              : 'Du darfst Buchungen dieser Fahrzeuggruppe freigeben und die Zeiten deshalb in beide Richtungen ändern.'}
        </p>

        {/*
          Beginn und Ende untereinander statt nebeneinander: In zwei Spalten
          eines Dialogs bleiben pro Paar rund 230 Pixel, und davon braucht das
          Zeitfeld schon die Hälfte. Für „TT.MM.JJJJ" samt Kalendersymbol reicht
          der Rest nicht.
        */}
        <div className="space-y-3">
          <Field label="Beginn">
            <DateTimeInput
              label="Beginn"
              date={startDate}
              time={startTime}
              onDateChange={setStartDate}
              onTimeChange={setStartTime}
              minDate={shrinkOnly ? dateValue(original.start) : undefined}
              stepSeconds={SLOT_MINUTES * 60}
            />
          </Field>

          <Field label="Ende">
            <DateTimeInput
              label="Ende"
              date={endDate}
              time={endTime}
              onDateChange={setEndDate}
              onTimeChange={setEndTime}
              maxDate={shrinkOnly ? dateValue(original.end) : undefined}
              stepSeconds={SLOT_MINUTES * 60}
            />
          </Field>
        </div>

        {problems.length > 0 && (
          <ul className="list-inside list-disc rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}

        {save.isError && <ErrorNotice error={save.error} />}

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={problems.length > 0 || unchanged || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Wird gespeichert …' : 'Zeiten ändern'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

